import { offlineDb } from '@/db/offlineDb';
import { supabase } from '@/integrations/supabase/client';
import OfflineLogger from '@/utils/offlineLogger';

interface DuplicateProductLookupParams {
  cleanData: any;
  tableName: string;
  loggerContext: string;
  fallbackUserId?: string | null;
}

interface DuplicateProductResolutionParams extends DuplicateProductLookupParams {
  entityId: string;
  queueItemId?: number;
}

interface DuplicateProductResolutionResult {
  resolved: boolean;
  resolvedProductId?: string;
}

export async function resolveDuplicateProductConflict({
  cleanData,
  entityId,
  queueItemId,
  tableName,
  loggerContext,
  fallbackUserId
}: DuplicateProductResolutionParams): Promise<DuplicateProductResolutionResult> {
  return resolveDuplicateProduct({
    cleanData,
    entityId,
    queueItemId,
    tableName,
    loggerContext,
    fallbackUserId
  });
}

export async function resolveDuplicateProductPreemptively({
  cleanData,
  entityId,
  queueItemId,
  tableName,
  loggerContext,
  fallbackUserId
}: DuplicateProductResolutionParams): Promise<DuplicateProductResolutionResult> {
  return resolveDuplicateProduct({
    cleanData,
    entityId,
    queueItemId,
    tableName,
    loggerContext,
    fallbackUserId
  });
}

async function resolveDuplicateProduct({
  cleanData,
  entityId,
  queueItemId,
  tableName,
  loggerContext,
  fallbackUserId
}: DuplicateProductResolutionParams): Promise<DuplicateProductResolutionResult> {
  const existingProduct = await findExistingDuplicateProduct({
    cleanData,
    tableName,
    loggerContext,
    fallbackUserId
  });

  if (!existingProduct) {
    return { resolved: false };
  }

  return realignLocalProductWithExisting({
    existingProduct,
    entityId,
    queueItemId,
    loggerContext
  });
}

async function findExistingDuplicateProduct({
  cleanData,
  tableName,
  loggerContext,
  fallbackUserId
}: DuplicateProductLookupParams) {
  const userId = cleanData?.user_id || fallbackUserId;
  const productName = cleanData?.name;

  if (!userId || !productName) {
    OfflineLogger.warn(
      `${loggerContext}.resolveDuplicateProductConflict`,
      'Impossibile risolvere il conflitto: user_id o name mancanti'
    );
    return null;
  }

  try {
    // Il vincolo univoco violato è user_products_user_id_category_name_unique,
    // definito su (user_id, lower(name)) SENZA category_id: lo stesso nome non
    // può coesistere per un utente nemmeno in categorie diverse. Filtrare anche
    // per category_id (come prima) faceva mancare il record quando il duplicato
    // server aveva una categoria diversa → il 23505 veniva rilanciato a Sentry
    // (CERALIA-9B/9C #1521). Cerchiamo sulle stesse colonne del vincolo così il
    // record esistente viene sempre trovato e riconciliato in modo idempotente.
    const { data: existingProduct, error } = await supabase
      .from(tableName)
      .select('*')
      .eq('user_id', userId)
      .ilike('name', productName)
      .maybeSingle();

    if (error) {
      OfflineLogger.error(
        `${loggerContext}.resolveDuplicateProductConflict`,
        `Errore durante la ricerca del prodotto duplicato: ${error.message}`
      );
      return null;
    }

    if (!existingProduct) {
      OfflineLogger.warn(
        `${loggerContext}.resolveDuplicateProductConflict`,
        `Nessun prodotto corrispondente trovato per ${productName}`
      );
      return null;
    }

    return existingProduct;
  } catch (unexpectedError) {
    OfflineLogger.error(
      `${loggerContext}.resolveDuplicateProductConflict`,
      unexpectedError
    );
    return null;
  }
}

async function realignLocalProductWithExisting({
  existingProduct,
  entityId,
  queueItemId,
  loggerContext
}: {
  existingProduct: any;
  entityId: string;
  queueItemId?: number;
  loggerContext: string;
}): Promise<DuplicateProductResolutionResult> {
  try {
    const resolvedProductId = existingProduct.id as string;
    const timestamp = Date.now();

    const productToStore = {
      ...existingProduct,
      _syncStatus: 'synced' as const,
      _lastModified: timestamp
    };

    await offlineDb.products.put(productToStore);

    if (resolvedProductId !== entityId) {
      await realignProductReferences(entityId, resolvedProductId, timestamp, loggerContext);

      try {
        await offlineDb.products.delete(entityId);
      } catch (deleteError) {
        OfflineLogger.warn(
          `${loggerContext}.resolveDuplicateProductConflict`,
          `Impossibile eliminare il prodotto locale temporaneo ${entityId}: ${deleteError}`
        );
      }
    }

    if (queueItemId) {
      try {
        await offlineDb.syncQueue.update(queueItemId, {
          status: 'completed',
          errorDetail: null
        });
      } catch (queueError) {
        OfflineLogger.warn(
          `${loggerContext}.resolveDuplicateProductConflict`,
          `Impossibile aggiornare lo stato della queue per l'item ${queueItemId}: ${queueError}`
        );
      }
    }

    OfflineLogger.info(
      `${loggerContext}.resolveDuplicateProductConflict`,
      `Associato il prodotto locale ${entityId} all'id esistente ${resolvedProductId}`
    );

    return { resolved: true, resolvedProductId };
  } catch (unexpectedError) {
    OfflineLogger.error(
      `${loggerContext}.resolveDuplicateProductConflict`,
      unexpectedError
    );
    return { resolved: false };
  }
}

async function realignProductReferences(
  oldProductId: string,
  newProductId: string,
  timestamp: number,
  loggerContext: string
) {
  if (oldProductId === newProductId) {
    return;
  }

  try {
    await offlineDb.shoppingItems
      .where('product_id')
      .equals(oldProductId)
      .modify((item: any) => {
        item.product_id = newProductId;
        if ('_lastModified' in item) {
          item._lastModified = timestamp;
        }
      });
  } catch (error) {
    OfflineLogger.warn(
      `${loggerContext}.realignProductReferences`,
      `Impossibile aggiornare shoppingItems per il prodotto ${oldProductId}: ${error}`
    );
  }

  try {
    await offlineDb.recurringProducts
      .where('product_id')
      .equals(oldProductId)
      .modify((item: any) => {
        item.product_id = newProductId;
        if ('_lastModified' in item) {
          item._lastModified = timestamp;
        }
      });
  } catch (error) {
    OfflineLogger.warn(
      `${loggerContext}.realignProductReferences`,
      `Impossibile aggiornare recurringProducts per il prodotto ${oldProductId}: ${error}`
    );
  }

  try {
    await offlineDb.purchaseHistory
      .where('product_id')
      .equals(oldProductId)
      .modify((item: any) => {
        item.product_id = newProductId;
        if ('_lastModified' in item) {
          item._lastModified = timestamp;
        }
      });
  } catch (error) {
    OfflineLogger.warn(
      `${loggerContext}.realignProductReferences`,
      `Impossibile aggiornare purchaseHistory per il prodotto ${oldProductId}: ${error}`
    );
  }

  try {
    await offlineDb.shoppingHistory
      .where('product_id')
      .equals(oldProductId)
      .modify((item: any) => {
        item.product_id = newProductId;
        if ('_lastModified' in item) {
          item._lastModified = timestamp;
        }
      });
  } catch (error) {
    OfflineLogger.warn(
      `${loggerContext}.realignProductReferences`,
      `Impossibile aggiornare shoppingHistory per il prodotto ${oldProductId}: ${error}`
    );
  }

  try {
    await offlineDb.mostPurchasedCache
      .where('product_id')
      .equals(oldProductId)
      .modify((item: any) => {
        item.product_id = newProductId;
        if ('cached_at' in item) {
          item.cached_at = timestamp;
        }
      });
  } catch (error) {
    OfflineLogger.warn(
      `${loggerContext}.realignProductReferences`,
      `Impossibile aggiornare mostPurchasedCache per il prodotto ${oldProductId}: ${error}`
    );
  }

  try {
    await offlineDb.syncQueue
      .where('entityId')
      .equals(oldProductId)
      .modify((queueItem: any) => {
        queueItem.entityId = newProductId;
      });
  } catch (error) {
    OfflineLogger.warn(
      `${loggerContext}.realignProductReferences`,
      `Impossibile riallineare la syncQueue per il prodotto ${oldProductId}: ${error}`
    );
  }
}
